import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchServiceById } from '../services/api';

/* ── Get pricing type display (icon + label) ──────────────────────── */
const getPricingTypeDisplay = (pricingType) => {
  const pricingMap = {
    visiting: { icon: '🚗', label: 'Visiting Charges' },
    fixed: { icon: '✅', label: 'Fixed Price' },
    starting: { icon: '📈', label: 'Starting Price' },
    subscription: { icon: '🔄', label: 'Subscription' },
  };
  return pricingMap[pricingType] || { icon: '💰', label: 'Price' };
};

const ViewService = () => {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadService();
  }, [serviceId]);

  const loadService = async () => {
    try {
      setLoading(true);
      const response = await fetchServiceById(serviceId);
      // Extract data from API response wrapper
      const serviceData = response?.data || response;
      if (!serviceData) {
        setError('Service not found');
        return;
      }
      setService(serviceData);
    } catch (err) {
      console.error('Error loading service:', err);
      setError('Failed to load service');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="section" style={{textAlign: 'center', padding: '100px'}}><div className="loader"></div></div>;

  if (error || !service) {
    return (
      <div className="section" style={{ maxWidth: '500px', textAlign: 'center', padding: '80px 20px' }}>
        <div style={{fontSize: '48px', color: 'var(--text-3)', marginBottom: '16px'}}>ℹ️</div>
        <h2>Service Not Found</h2>
        <button className="btn btn-primary" style={{marginTop: '20px'}} onClick={() => navigate('/')}>Back to Home</button>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingBottom: '80px' }}>
      <style>{`
        .service-layout { display: grid; grid-template-columns: 1fr 360px; gap: 32px; }
        .info-card { background: white; border-radius: 16px; border: 1px solid var(--border); padding: 24px; margin-bottom: 24px; }
        .tag-badge { padding: 4px 12px; background: #e2e8f0; border-radius: 20px; fontSize: 12px; color: #475569; font-weight: 600; }
        .sticky-side { position: sticky; top: 24px; }
        .mobile-actions { display: none; position: fixed; bottom: 0; left: 0; right: 0; background: white; padding: 12px 20px; box-shadow: 0 -4px 20px rgba(0,0,0,0.1); z-index: 1000; gap: 12px; }
        
        @media (max-width: 900px) {
          .service-layout { grid-template-columns: 1fr; gap: 20px; }
          .sticky-side { position: static; }
          .desktop-sidebar { display: none; }
          .mobile-actions { display: flex; }
        }
      `}</style>

      {/* Top Header */}
      <div style={{background: 'white', borderBottom: '1px solid var(--border)', marginBottom: '24px'}}>
        <div className="section" style={{padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <button onClick={() => navigate(-1)} style={{background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-2)', fontWeight: 600}}>
            ← Back
          </button>
          <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: 'var(--primary)', fontWeight: 600}}>
            👁 View Service
          </div>
          <div style={{display: 'flex', gap: '8px'}}>
             <button className="btn btn-secondary btn-sm" onClick={() => {
               navigator.clipboard.writeText(window.location.href);
               alert('Link copied!');
             }}>📤</button>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="service-layout">
          
          {/* LEFT: Content */}
          <main>
            <div className="info-card">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px'}}>
                <span style={{padding: '4px 12px', background: 'rgba(79, 70, 229, 0.1)', color: 'var(--primary)', borderRadius: '6px', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase'}}>
                  {service.category?.name}
                </span>
                {service.rating && (
                  <div style={{display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 700}}>
                    ⭐ {service.rating}
                  </div>
                )}
              </div>
              
              <h1 style={{fontSize: '2rem', fontWeight: 800, marginBottom: '16px', color: 'var(--text-1)'}}>{service.name}</h1>
              
              {service.pricing_type && (
                <div style={{display: 'inline-block', padding: '6px 12px', background: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 600, color: '#1e40af', marginBottom: '16px'}}>
                  {getPricingTypeDisplay(service.pricing_type).icon} {getPricingTypeDisplay(service.pricing_type).label}
                </div>
              )}
              
              <div style={{display: 'flex', alignItems: 'center', gap: '16px', color: 'var(--text-3)', fontSize: '0.95rem', marginBottom: '24px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>📍 {service.area?.name}, {service.city?.name}</div>
                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>🕐 {service.hours || 'Contact for hours'}</div>
              </div>

              <div style={{padding: '20px', background: '#f1f5f9', borderRadius: '12px', marginBottom: '24px'}}>
                <div style={{fontSize: '0.85rem', color: 'var(--text-3)', marginBottom: '4px'}}>{getPricingTypeDisplay(service.pricing_type).label}</div>
                <div style={{fontSize: '1.75rem', fontWeight: 800, color: 'var(--primary)'}}>₹{service.price}</div>
              </div>

              <h3 style={{fontSize: '1.1rem', fontWeight: 700, marginBottom: '12px'}}>Description</h3>
              <p style={{lineHeight: '1.7', color: 'var(--text-2)', whiteSpace: 'pre-line'}}>{service.description}</p>
              
              {/* Tags Section */}
              {service.tags && service.tags.length > 0 && (
                <div style={{marginTop: '24px', display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                  {service.tags.map(tag => (
                    <span key={tag.id} className="tag-badge">#{tag.name}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Vendor Profile Card */}
            <div className="info-card">
              <div style={{display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px'}}>
                <div style={{width: '64px', height: '64px', background: 'var(--accent)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 800}}>
                  {service.vendor?.name?.charAt(0)}
                </div>
                <div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                    <h3 style={{margin: 0, fontSize: '1.1rem'}}>{service.vendor?.name}</h3>
                    <span style={{color: '#0ea5e9', fontSize: '18px', title: 'Verified Provider'}}>✅</span>
                  </div>
                  <p style={{margin: 0, color: 'var(--text-3)', fontSize: '0.9rem'}}>{service.vendor?.business_name || 'Individual Professional'}</p>
                </div>
              </div>
              
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
                <div style={{fontSize: '0.9rem'}}>
                  <div style={{color: 'var(--text-3)', marginBottom: '2px'}}>Location</div>
                  <div style={{fontWeight: 600}}>{service.vendor?.address || 'Not specified'}</div>
                </div>
                <div style={{fontSize: '0.9rem'}}>
                  <div style={{color: 'var(--text-3)', marginBottom: '2px'}}>Response Time</div>
                  <div style={{fontWeight: 600}}>Usually within 1 hour</div>
                </div>
              </div>

              <button className="btn btn-secondary" style={{width: '100%', marginTop: '20px'}} onClick={() => navigate(`/vendor/${service.vendor?.id}`)}>
                View Provider Profile
              </button>
            </div>
          </main>

          {/* RIGHT: Desktop Sidebar */}
          <aside className="desktop-sidebar">
            <div className="sticky-side">
              <div className="info-card" style={{border: '2px solid var(--primary)'}}>
                <h3 style={{marginTop: 0, marginBottom: '16px', fontSize: '1.1rem'}}>Interested in this?</h3>
                <p style={{fontSize: '0.9rem', color: 'var(--text-3)', marginBottom: '20px'}}>Contact the provider directly to book or ask questions.</p>
                
                <a href={`tel:${service.user?.phone}`} style={{textDecoration: 'none'}}>
                  <button className="btn btn-primary" style={{width: '100%', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                    📞 Call Provider
                  </button>
                </a>
                
                <a href={`https://wa.me/${service.user?.whatsapp}`} target="_blank" rel="noreferrer" style={{textDecoration: 'none'}}>
                  <button className="btn" style={{width: '100%', background: '#25D366', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                    💬 WhatsApp
                  </button>
                </a>
              </div>
              
              <div style={{textAlign: 'center', padding: '10px', color: 'var(--text-3)', fontSize: '0.8rem'}}>
                ID: REF-{service.id}00{service.user_id}
              </div>
            </div>
          </aside>

        </div>
      </div>

      {/* MOBILE: Bottom Sticky Bar */}
      <div className="mobile-actions">
        <a href={`tel:${service.user?.phone}`} style={{flex: 1, textDecoration: 'none'}}>
          <button className="btn btn-primary" style={{width: '100%', padding: '14px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
            📞 Call
          </button>
        </a>
        <a href={`https://wa.me/${service.user?.whatsapp}`} style={{flex: 1, textDecoration: 'none'}}>
          <button className="btn" style={{width: '100%', background: '#25D366', color: 'white', border: 'none', padding: '14px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
            💬 WhatsApp
          </button>
        </a>
      </div>
    </div>
  );
};

export default ViewService;